import React from "react";

import { getComponentModule } from "components/ComponentType";
import {createRoot, Root} from "react-dom/client";
import {initUser, useUserState} from "../store/user-store";
import {MissingComponentWarning} from "./MissingComponentWarning";
import {setupLocale} from "../services/lang/LanguageService";
import {DashboardComponentType} from "../api/model/dashboard/DashboardComponent";
import ComponentRoot from "./ComponentRoot";

export const COMPONENT_ATTRIBUTE = "jsComponent";
export const COMPONENT_SELECTOR = "[jsComponent]";
export const PROPS_ATTRIBUTE = "props";
export const INVISIBLE_ON_MISSING_ATTRIBUTE = "invisibleOnMissing";
const COMPONENT_IS_ROOT_FLAG = "_isComponentRoot";

declare global {
  interface Window {
    reactRoots: Map<string, IReactRootEl>;
  }
}

interface IReactRootEl {
  el: Element;
  root: Root;
}

// Component map [id -> HTML Element]
window.reactRoots = window.reactRoots || new Map<string, IReactRootEl>();


/**
 * Renders a component into a DOM element. This is main entry point for rendering components.
 * @param root
 * @param importPromise Promise that resolves to a React component
 * @param props Props to pass to the component
 */
const renderComponent = (
  id: string,
  type: string,
  root: Root,
  importPromise: any,
  props: any
) => {
  // @ts-ignore
  importPromise.then(async module => {
    const componentRoot = <ComponentRoot id={id} type={type} module={module} props={props} />;

    root.render(componentRoot);
  });
};

export const mountElementRoot = (el: Element, elType?: string, elProps?: any) => {
  const { id } = el;
  const type = elType ?? el.getAttribute(COMPONENT_ATTRIBUTE);
  const props = elProps ?? parseJsonProps(el.getAttribute(PROPS_ATTRIBUTE)!);
  const invisibleOnMissing = Boolean(el.getAttribute(INVISIBLE_ON_MISSING_ATTRIBUTE))

  let rootObj = window.reactRoots.get(id);
  if (rootObj) {
    if (rootObj.el !== el) { // Element reference changed (cause: JSF tree was replaced)
      rootObj!!.root.unmount();
      const oldType = rootObj?.el.getAttribute(COMPONENT_ATTRIBUTE);
      console.log(`Unmounted root [${oldType}] component at [${id}] to be remounted.`);
      rootObj = undefined;
    } else { // Element reference is the same, we can reuse the root (cause: HMR)
      console.log(`Will be reusing root for [${type}] component at [${id}].`);
    }
  }

  if(!rootObj) {
    console.log(`Creating new root for [${type}] component at [${id}].`);
    rootObj = { el, root: createRoot(el, { identifierPrefix: `${id}_` }) };
    el.setAttribute(COMPONENT_IS_ROOT_FLAG, "true");
    window.reactRoots.set(id, rootObj);
  }

  const importPromise = getComponentModule(type as DashboardComponentType);

  if (importPromise) {
    renderComponent(id, type!, rootObj.root, importPromise, props);
  } else {
    console.log(`Unknown component! ${type}`);
    if (invisibleOnMissing) {
      rootObj.root.render(<span/>);
  } else {
      rootObj.root.render(<MissingComponentWarning componentId={type} />);
    }
  }
};

const parseJsonProps = (jsonProps: string) => {
  try {
    return JSON.parse(jsonProps);
  } catch {
    return {};
  }
};

export const initEasasJs = async () => {
  const script = document.currentScript as HTMLScriptElement

  window.jsBasePath = script.src.replace(/\/[^/]+$/, "");

  initUser()
  await setupLocale(useUserState.getState())

  const els = Array.from(document.querySelectorAll(COMPONENT_SELECTOR));

  els.forEach(el => {
    mountElementRoot(el);
  });
};
